import { WEATHER_LAT, WEATHER_LON } from '../constants/weather';
import {
  buildRadarReflectivityLoopLayers,
  type RadarLoopFrame,
  IEM_RADAR_JSON,
} from '../utils/iemRadarUrls';

export type IemRadarSite = {
  id: string;
  name: string;
  lat: number;
  lon: number;
};

type AvailableResponse = {
  radars?: Array<{
    id: string;
    name: string;
    lat: number;
    lon: number;
    type: string;
  }>;
};

export function haversineMiles(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const r = 3958.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(a));
}

/** Nearest NEXRAD site to property coordinates (IEM radar id, e.g. EWX). */
export async function resolveNearestIemRadar(
  lat = WEATHER_LAT,
  lon = WEATHER_LON,
  signal?: AbortSignal
): Promise<IemRadarSite> {
  const url = `${IEM_RADAR_JSON}?operation=available&lat=${lat}&lon=${lon}`;
  const res = await fetch(url, signal ? { signal } : undefined);
  if (!res.ok) throw new Error(`IEM radar lookup failed (${res.status})`);
  const body = (await res.json()) as AvailableResponse;
  const sites = (body.radars ?? []).filter(r => r.type === 'NEXRAD');
  if (sites.length === 0) throw new Error('No NEXRAD sites found for this location.');

  let best = sites[0]!;
  let bestDist = haversineMiles(lat, lon, best.lat, best.lon);
  for (let i = 1; i < sites.length; i++) {
    const s = sites[i]!;
    const d = haversineMiles(lat, lon, s.lat, s.lon);
    if (d < bestDist) {
      best = s;
      bestDist = d;
    }
  }
  return { id: best.id, name: best.name, lat: best.lat, lon: best.lon };
}

export type RadarLoopFrames = {
  /** Oldest first (CONUS base reflectivity mosaic). */
  frames: RadarLoopFrame[];
};

/** Last-hour reflectivity loop using IEM CONUS NEXRAD mosaic on IEM. */
export async function loadRadarLoopFrames(
  _lat = WEATHER_LAT,
  _lon = WEATHER_LON,
  signal?: AbortSignal
): Promise<RadarLoopFrames> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  return { frames: buildRadarReflectivityLoopLayers() };
}
